import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  DisagreeEvaluationMutationDocument,
  GetEvaluationAgreementsDocument,
  PerformanceReviewsEvaluationAgreement,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  evaluationId: number
  agreement?: PerformanceReviewsEvaluationAgreement
  canEditAgreement: boolean
  onClose: () => void
}

const DisagreeEvaluationModal = ({ evaluationId, agreement, canEditAgreement, onClose }: Props) => {
  const { access } = useCurrentSession()
  const [comment, setComment] = React.useState<string | null>(
    agreement && agreement.comment ? agreement.comment : ''
  )
  const [createDisagreement] = useMutation(DisagreeEvaluationMutationDocument, {
    extractorFn: (data) =>
      data.performanceReviewsMutations.disagreePerformancereviewsEvaluationAgreement,
  })

  const { running, performAction: onSubmit } = useAsyncAction(async () => {
    const response = await createDisagreement({
      awaitRefetchQueries: true,
      variables: {
        comment: comment ?? '',
        authorId: access.id,
        evaluationId: evaluationId,
      },
      refetchQueries: [
        {
          query: GetEvaluationAgreementsDocument,
          variables: {
            evaluation_id: evaluationId,
          },
        },
      ],
    })

    const handled = handleResult(response, () => {
      // TODO: do something with form errors
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
    return handled
  })

  const description = canEditAgreement
    ? i18n.t('surveys.review.reviews.agreements_section.modal.description.disagreement.target_user')
    : i18n.t(
        'surveys.review.reviews.agreements_section.modal.description.disagreement.respondent_user'
      )

  return (
    <Modal
      illustration="window-user"
      title={i18n.t('surveys.review.reviews.agreements_section.modal.title.disagreement')}
      description={description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        canEditAgreement ? (
          <>
            <Button type="secondary" onClick={() => onClose()}>
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={running || !comment?.length} submit>
              {i18n.t('common.save')}
            </Button>
          </>
        ) : null
      }
    >
      <TextArea
        readonly={!canEditAgreement}
        placeholder={i18n.t('surveys.review.reviews.agreements_section.modal.placeholder')}
        value={comment as string}
        onChange={(value) => {
          if (setComment) {
            setComment(value)
          }
        }}
      />
    </Modal>
  )
}

export default observer(DisagreeEvaluationModal)
